/**
 * BusinessNotifications.jsx — Notifications inbox for the business portal.
 *
 * Displays procurement, delivery, supplier operations, and other
 * business-scoped notifications with filters, unread tracking,
 * infinite scroll, and action buttons.
 */
import { useRef, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Package, Truck, Info, Check, CheckCheck, Loader2,
  Filter, UserPlus, X, ShieldAlert,
} from "lucide-react";
import useBusinessNotifications from "../hooks/useBusinessNotifications";
import { timeAgo } from "../../utils/timeAgo";
import { useTimeRefresh } from "../../hooks/useTimeRefresh";
import { PageHeader } from "../../components/erp";
import { resolveBusinessNotificationUrl } from "../../utils/notificationLinks";
import ShopSelect from "../../components/ShopSelect";

/* ═══════════════════════════════════════════════
   Category → icon + colour mapping (business-relevant)
   ═══════════════════════════════════════════════ */
const CAT_META = {
  PROCUREMENT:  { Icon: Package,  color: "text-indigo-500",  bg: "bg-indigo-50  dark:bg-indigo-900/30" },
  DELIVERY:     { Icon: Truck,    color: "text-sky-500",     bg: "bg-sky-50     dark:bg-sky-900/30" },
  SUPPLIER_OPS: { Icon: UserPlus, color: "text-teal-500",    bg: "bg-teal-50    dark:bg-teal-900/30" },
  INVITATION:   { Icon: UserPlus, color: "text-violet-500",  bg: "bg-violet-50  dark:bg-violet-900/30" },
  SYSTEM:       { Icon: Info,     color: "text-neutral-500",  bg: "bg-neutral-100 dark:bg-neutral-800" },
};

const CATEGORY_OPTIONS = [
  { value: "",             label: "All Categories" },
  { value: "PROCUREMENT",  label: "Procurement" },
  { value: "DELIVERY",     label: "Delivery & Logistics" },
  { value: "INVITATION",   label: "Invitations" },
  { value: "SUPPLIER_OPS", label: "Supplier Operations" },
];

/* ═══════════════════════════════════════════════
   Main component
   ═══════════════════════════════════════════════ */
export default function BusinessNotifications() {
  const navigate = useNavigate();
  useTimeRefresh(30_000);

  const {
    items, meta, loading, hasMore, unreadCount,
    filter, setFilter,
    category, setCategory,
    loadMore,
    handleMarkRead, handleMarkAllRead,
  } = useBusinessNotifications();

  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    if (!actionError) return;
    const t = setTimeout(() => setActionError(null), 4000);
    return () => clearTimeout(t);
  }, [actionError]);

  /* ── Infinite scroll ── */
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loading) loadMore(); },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadMore]);

  /* ── Render a notification row ── */
  const renderItem = useCallback((n) => {
    const catMeta = CAT_META[n.category] || CAT_META.SYSTEM;
    const { Icon, color, bg } = catMeta;
    const isRead = n.isRead || n.is_read;
    const targetUrl = resolveBusinessNotificationUrl(n);

    return (
      <div
        key={n.id}
        role="button"
        tabIndex={0}
        onClick={() => {
          if (!isRead) handleMarkRead(n.id);
          if (targetUrl) navigate(targetUrl);
        }}
        onKeyDown={(e) => e.key === "Enter" && targetUrl && navigate(targetUrl)}
        className={[
          "group relative flex gap-3 rounded-xl p-3 transition cursor-pointer",
          isRead
            ? "bg-transparent hover:bg-neutral-50 dark:hover:bg-neutral-800/50"
            : "bg-neutral-50 dark:bg-neutral-800/60 hover:bg-neutral-100 dark:hover:bg-neutral-800",
        ].join(" ")}
      >
        {/* Icon */}
        <div className={`shrink-0 flex items-center justify-center h-9 w-9 rounded-full ${bg}`}>
          <Icon size={16} className={color} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pr-5">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className={[
                "text-sm font-medium leading-snug truncate",
                isRead
                  ? "text-neutral-600 dark:text-neutral-400"
                  : "text-neutral-900 dark:text-neutral-50",
              ].join(" ")}>
                {n.title}
              </p>
              <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400 line-clamp-2">
                {n.body || n.summary}
              </p>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-1">
              <span className="text-[10px] text-neutral-400 dark:text-neutral-500 whitespace-nowrap">
                {timeAgo(n.createdAt || n.created_at)}
              </span>
              {!isRead && <span className="h-2 w-2 rounded-full bg-blue-500" title="Unread" />}
            </div>
          </div>

          {/* Mark as read */}
          {!isRead && (
            <div className="mt-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleMarkRead(n.id); }}
                className="inline-flex items-center gap-1 text-[10px] font-medium tracking-wider uppercase
                           text-neutral-500 hover:text-neutral-800
                           dark:text-neutral-400 dark:hover:text-neutral-200 transition"
              >
                <Check size={11} /> Mark as read
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }, [handleMarkRead, navigate]);

  /* ── Toolbar ── */
  const toolbar = (
    <button
      type="button"
      onClick={handleMarkAllRead}
      disabled={unreadCount === 0}
      className="inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5
                 text-[11px] font-semibold tracking-wider uppercase
                 border-neutral-200 text-neutral-600 hover:bg-neutral-100
                 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800
                 disabled:opacity-40 disabled:cursor-not-allowed transition"
    >
      <CheckCheck size={13} /> Mark all read
    </button>
  );

  /* ── Filter bar ── */
  const filterBar = (
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <div className="flex rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        {["all", "unread"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={[
              "px-3 py-1.5 text-xs font-semibold capitalize transition",
              filter === f
                ? "bg-neutral-900 text-white dark:bg-neutral-100 dark:text-neutral-900"
                : "bg-white text-neutral-600 hover:bg-neutral-50 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800",
            ].join(" ")}
          >
            {f === "unread" ? `Unread (${unreadCount})` : "All"}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400">
        <Filter size={13} />
      </div>
      <ShopSelect
        value={category}
        onValueChange={setCategory}
        options={CATEGORY_OPTIONS}
        ariaLabel="Filter notifications by category"
        align="start"
        minPanelWidth={176}
        triggerClassName="min-h-8 w-44 rounded-lg px-2.5 py-1 text-xs"
        panelClassName="py-1"
        optionClassName="text-xs"
      />
    </div>
  );

  /* ── Empty state ── */
  const emptyState = (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-neutral-100 dark:bg-neutral-800 p-4 mb-4">
        <Bell size={28} className="text-neutral-400" />
      </div>
      <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
        {filter === "unread" ? "No unread notifications" : "No notifications yet"}
      </p>
      <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
        {filter === "unread"
          ? "You're all caught up!"
          : "Business notifications about procurement, deliveries, and supplier activity will appear here."}
      </p>
    </div>
  );

  /* ── Skeleton ── */
  const skeleton = (
    <div className="space-y-3">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="flex gap-3 animate-pulse">
          <div className="h-9 w-9 rounded-full bg-neutral-200 dark:bg-neutral-700 shrink-0" />
          <div className="flex-1 space-y-2 py-1">
            <div className="h-3 w-1/3 rounded bg-neutral-200 dark:bg-neutral-700" />
            <div className="h-3 w-2/3 rounded bg-neutral-200 dark:bg-neutral-700" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        subtitle={meta?.total != null ? `${meta.total} notification${meta.total !== 1 ? "s" : ""}` : "Business notifications"}
      >
        {toolbar}
      </PageHeader>

      <div className="rounded-2xl border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900">
        {/* Error toast */}
        {actionError && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50
                          px-3 py-2 text-xs text-red-700
                          dark:border-red-800/50 dark:bg-red-950/30 dark:text-red-300">
            <ShieldAlert size={14} className="shrink-0" />
            <span className="flex-1">{actionError}</span>
            <button type="button" onClick={() => setActionError(null)} className="p-0.5 hover:opacity-70">
              <X size={12} />
            </button>
          </div>
        )}

        {filterBar}

        {loading && items.length === 0
          ? skeleton
          : items.length === 0
            ? emptyState
            : (
              <div className="space-y-1">
                {items.map(renderItem)}

                <div ref={sentinelRef} className="h-1" />

                {loading && (
                  <div className="flex justify-center py-4">
                    <Loader2 size={20} className="animate-spin text-neutral-400" />
                  </div>
                )}

                {!hasMore && items.length > 0 && (
                  <p className="text-center text-[11px] text-neutral-400 dark:text-neutral-500 py-3">
                    You've reached the end
                  </p>
                )}
              </div>
            )
        }
      </div>
    </div>
  );
}
